import type { StoreActionApi } from 'react-sweet-state';
import { createStore, createHook } from 'react-sweet-state';

type ContentId = string;

interface ContentTreeSelectionState {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	root: any | null; // Page tree root
	count: number; // Count represents the number of selected content items and its descendants (if applicable).
	isMultiSelectActive: boolean;
	selectedContent: Record<ContentId, { selectedDescendantCount: number }>;
}

const initialState: ContentTreeSelectionState = {
	root: null,
	isMultiSelectActive: false,
	selectedContent: {},
	count: 0,
};

const actions = {
	enterMultiSelect:
		() =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({
				isMultiSelectActive: true,
				selectedContent: {},
				count: 0,
			});
		},
	exitMultiSelect:
		() =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({
				isMultiSelectActive: false,
				selectedContent: {},
				root: null,
				count: 0,
			});
		},
	setRoot:
		(root) =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({ root });
		},
	selectContent:
		(contentId: ContentId) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { selectedContent } = getState();
			const newSelectedContent = {
				...selectedContent,
				[contentId]: {
					selectedDescendantCount: 0, // value from archivableDescendantsCount
				},
			};

			setState({
				selectedContent: newSelectedContent,
				count: getState().count + 1, // Increment parent and nested children count
			});
		},
	unselectContent:
		(contentId: ContentId) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { selectedContent } = getState();

			const { [contentId]: _, ...rest } = selectedContent;

			setState({
				selectedContent: rest,
				count: getState().count - 1, // Decrement parent and nested children count
			});
		},
	clearSelection:
		() =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({
				selectedContent: {},
				count: 0,
			});
		},
};

const Store = createStore<ContentTreeSelectionState, typeof actions>({
	initialState,
	actions,
	name: 'contentTreeSelection',
});

export const useContentTreeSelection = createHook(Store);
